import * as creditCardType from "credit-card-type";
import { FieldProps } from "formik";
import { fill, get } from "lodash";
import React, { useEffect, useState } from "react";
import Icon from "../../../Icon";
import MaskedInput from "../MaskedInput";

export interface CreditCardInputProps {
	field: FieldProps["field"];
	form: FieldProps["form"];
	id: string;
	name: string;
	onKeyPress?: (e: React.KeyboardEvent) => void;
	ref?: React.RefObject<any>;
	value?: string;
	[key: string]: any;
	editInline?: boolean;
	className?: string;
	receiveCardType?: (cardName?: string) => void;
}

export interface CreditCardType {
	niceType: string;
	type: string;
	patterns: number[] | number[][];
	gaps: number[];
	lengths: number[];
	code: {
		size: number;
		name: string;
	};
	matchStrength?: number;
}

type CreditCardMask = (string | RegExp)[];

const defaultMask: CreditCardMask = [
	/\d/,
	/\d/,
	/\d/,
	/\d/,
	"\u0020",
	/\d/,
	/\d/,
	/\d/,
	/\d/,
	"\u0020",
	/\d/,
	/\d/,
	/\d/,
	/\d/,
	"\u0020",
	/\d/,
	/\d/,
	/\d/,
	/\d/,
];

interface IconMap {
	[key: string]: string;
}

const CreditCardInput: React.FC<CreditCardInputProps> = ({
	type = "text",
	field,
	form,
	className,
	editInline,
	receiveCardType,
	...props
}) => {
	const [currentCardInfo, setCurrentCardInfo] = useState<CreditCardType | null>(null);
	const [icon, setIcon] = useState("credit-card");

	const iconMap: IconMap = {
		visa: "cc-visa",
		mastercard: "cc-mastercard",
		"american-express": "cc-amex",
		discover: "cc-discover",
		jcb: "cc-jcb",
		"diners-club": "cc-diners-club",
	};

	const getCardType = (value: string) => {
		const cardInfo = creditCardType.default(value);
		if (value && cardInfo && currentCardInfo?.type != cardInfo[0]?.type) {
			setCurrentCardInfo(cardInfo[0]);
			return cardInfo[0];
		}
	};
	const createMask = (rawValue: string) => {
		const cardInfo = getCardType(rawValue);
		if (cardInfo) {
			let maxLength = cardInfo.lengths[cardInfo.lengths.length - 1];
			if (maxLength > 16) {
				maxLength = 16;
			}
			const maskArray = fill(Array(maxLength), RegExp(/\d/)) as CreditCardMask;
			for (let i = maskArray.length; i >= 0; i--) {
				if (cardInfo.gaps.includes(i)) {
					maskArray.splice(i, 0, "\u0020");
				}
			}
			return maskArray;
		} else {
			return defaultMask;
		}
	};

	useEffect(() => {
		if (currentCardInfo?.type && iconMap[currentCardInfo?.type]) {
			setIcon(iconMap[currentCardInfo?.type]);
			if (receiveCardType) {
				receiveCardType(currentCardInfo?.type);
			}
		} else {
			setIcon("credit-card");
			if (receiveCardType) {
				receiveCardType(undefined);
			}
		}
	}, [currentCardInfo]);

	const errorValue = get(form.errors, field.name);
	const touchedValue = get(form.touched, field.name);

	return (
		<div className="input-group credit-card-input-group">
			<span
				className={`${errorValue && touchedValue ? "error-icon " : ""}credit-card-icon ${
					field.value ? "active" : ""
				}`}
			>
				<Icon icon={icon} type={icon === "credit-card" ? "regular" : "brand"} />
			</span>
			<MaskedInput
				mask={(rawValue: string) => createMask(rawValue)}
				onInput={(e: any) => {
					getCardType(e.target.value);
				}}
				placeholder="Card number"
				placeholderChar={"\u2002"}
				{...props}
				{...field}
				className={`${className ? className : ""}${editInline ? "edit-inline" : ""}`}
			></MaskedInput>
		</div>
	);
};

export default CreditCardInput;
